import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { usersService } from '@services/users.service';
import type { UserSummaryResponse } from '@apptypes/index';

function apiMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown };
    if (typeof e.message === 'string' && e.message.trim()) return e.message;
  }
  return fallback;
}

const input = 'w-full rounded-xl border border-[var(--border-default)] bg-[var(--bg-base)] px-3 py-2 text-sm text-[var(--text-primary)] outline-none';

export function UserEditModal({ user, onClose, onSaved }: {
  user: UserSummaryResponse; onClose: () => void; onSaved: () => void;
}) {
  const [form, setForm] = useState({
    firstName: user.firstName ?? '',
    lastName: user.lastName ?? '',
    email: user.email ?? '',
    phone: user.phone ?? '',
  });
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: () => usersService.update(user.userId, form),
    onSuccess: () => { toast.success('User updated.'); onSaved(); },
    onError: (e) => toast.error(apiMessage(e, 'Could not update the user.')),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="mt-4 w-full max-w-md rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-semibold text-[var(--text-primary)]">Edit user</h3>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"><X size={18} /></button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="First name"><input value={form.firstName} onChange={set('firstName')} className={input} /></Field>
            <Field label="Last name"><input value={form.lastName} onChange={set('lastName')} className={input} /></Field>
          </div>
          <Field label="Phone"><input value={form.phone} onChange={set('phone')} className={input} placeholder="+2547XXXXXXXX" /></Field>
          <Field label="Email"><input value={form.email} onChange={set('email')} className={input} type="email" /></Field>
          <p className="text-[12px] text-[var(--text-muted)]">A user must keep at least a phone or an email.</p>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="rounded-xl px-4 py-2 text-sm font-semibold text-[var(--text-muted)]">Cancel</button>
            <button disabled={save.isPending} onClick={() => save.mutate()}
              className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--text-primary)] px-4 py-2 text-sm font-semibold text-[var(--bg-surface)] disabled:opacity-50">
              {save.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1.5 block text-[13px] font-medium text-[var(--text-secondary)]">{label}</label>
      {children}
    </div>
  );
}
